import { useState, useEffect, useRef } from "react";
import { trackMetaPageView } from "@/lib/meta-pixel";
import { captureAttribution } from "@/lib/attribution";
import { fireTrialConversions } from "@/lib/conversions";
import { captureCid, notifyTrialStarted } from "@/lib/funnel";
import { Switch, Route, Redirect, useRoute, useLocation } from "wouter";
import { queryClient, apiRequest } from "./lib/queryClient";
import { QueryClientProvider, useQuery, useMutation } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary, GlobalErrorFallback } from "@/components/error-boundary";
import { TourProvider, useTour } from "@/components/guided-tour/tour";
import { TOURS } from "@/lib/tours";
import { HelpCircle, Building2 } from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { getNewlyCompletedPhase } from "@/lib/rtd-phases";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import GeoGridPage from "@/pages/geogrid";
import PostsPage from "@/pages/posts";
import ReviewsPage from "@/pages/reviews";
import ListingPage from "@/pages/listing";
import NotificationsPage from "@/pages/notifications";
import SettingsPage from "@/pages/settings";
import LeaderboardPage from "@/pages/leaderboard";
import ClientsPage from "@/pages/clients";
import SharePage from "@/pages/share";

import RoadToDominationPage from "@/pages/road-to-domination";
import LevelFifteenPage from "@/pages/level-fifteen";
import MarketPlaceProductPage from "@/pages/marketplace-product";
import GbpOptimizationPage from "@/pages/gbp-optimization";
import ProfileHealthPage from "@/pages/profile-health";
import CitationsPage from "@/pages/citations";
import SchemaPage from "@/pages/schema";
import ServicesStackPage from "@/pages/services-stack";
import SocialPage from "@/pages/social";

import AutomationPage from "@/pages/automation";
import AdminPage from "@/pages/admin";
import ReportsPage from "@/pages/reports";
import ClientReportPage from "@/pages/client-report";
import SetupPage from "@/pages/setup";
import AuthPage from "@/pages/auth-page";
import AdminLoginPage from "@/pages/admin-login";
import HomePage from "@/pages/homepage";
import TrialLandingPage, { META_TRIAL_HERO } from "@/pages/trial-landing";
import type { HeroCopy } from "@/pages/homepage";
import SeoPage, { SeoForHub, SeoNichePage, SeoBestHub, SeoBestPage } from "@/pages/seo-page";
import { TORK_SLUGS } from "@/lib/tork/pages";
import TestimonialsPage from "@/pages/testimonials";
import MediaPage from "@/pages/media";
import ProductsPage from "@/pages/products";
import WebsitePage from "@/pages/website";
import PressReleasePage from "@/pages/press-release";
import ScanCreditsPage from "@/pages/scan-credits";
import PricingPage from "@/pages/pricing";
import CheckoutPage from "@/pages/checkout";
import LinkGbpPage from "@/pages/link-gbp";
import TermsPage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import AcceptTermsPage from "@/pages/accept-terms";
import RedeemPage, { REDEEM_STORAGE_KEY } from "@/pages/redeem";
import ReferralsPage from "@/pages/referrals";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeProvider } from "@/components/theme-provider";
import { AriaChat } from "@/components/aria-chat";
import { BugReporter } from "@/components/bug-reporter";
import { FeedbackButton } from "@/components/feedback-button";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { Location } from "@shared/schema";
import { posthog } from "@/lib/analytics";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useSessionDuration } from "@/hooks/useEngagementTime";
import { trackEvent } from "@/hooks/useTracking";

import { OnboardingContext } from "@/hooks/use-onboarding";

function Router() {
  return (
    <Switch>
      <Route path="/setup" component={SetupPage} />
      <Route path="/road-to-domination">{() => <ErrorBoundary section="Road to Domination"><RoadToDominationPage /></ErrorBoundary>}</Route>
      <Route path="/total-domination">{() => <ErrorBoundary section="Total Domination"><LevelFifteenPage /></ErrorBoundary>}</Route>
      <Route path="/marketplace/:productId">{() => <ErrorBoundary section="Market Place Product"><MarketPlaceProductPage /></ErrorBoundary>}</Route>
      <Route path="/dashboard">{() => <ErrorBoundary section="Dashboard"><Dashboard /></ErrorBoundary>}</Route>
      <Route path="/geogrid">{() => <ErrorBoundary section="GeoGrid"><GeoGridPage /></ErrorBoundary>}</Route>
      <Route path="/posts" component={PostsPage} />
      <Route path="/reviews" component={ReviewsPage} />

      <Route path="/gbp-optimization">{() => <ErrorBoundary section="GBP Optimization"><GbpOptimizationPage /></ErrorBoundary>}</Route>
      <Route path="/profile-health">{() => <ErrorBoundary section="Profile Essentials"><ProfileHealthPage /></ErrorBoundary>}</Route>
      <Route path="/citations">{() => <ErrorBoundary section="Citations"><CitationsPage /></ErrorBoundary>}</Route>
      <Route path="/schema">{() => <ErrorBoundary section="Schema"><SchemaPage /></ErrorBoundary>}</Route>
      <Route path="/services">{() => <ErrorBoundary section="Service Stacking"><ServicesStackPage /></ErrorBoundary>}</Route>
      <Route path="/social" component={SocialPage} />

      <Route path="/listing" component={ListingPage} />
      <Route path="/notifications" component={NotificationsPage} />
      <Route path="/leaderboard" component={LeaderboardPage} />
      <Route path="/automation" component={AutomationPage} />
      <Route path="/reports">{() => <ErrorBoundary section="Reports"><ReportsPage /></ErrorBoundary>}</Route>
      <Route path="/client-report"><Redirect to="/reports" /></Route>
      <Route path="/media" component={MediaPage} />
      <Route path="/products">{() => <ErrorBoundary section="Product Forge"><ProductsPage /></ErrorBoundary>}</Route>
      <Route path="/website">{() => <ErrorBoundary section="Website"><WebsitePage /></ErrorBoundary>}</Route>
      <Route path="/press-release">{() => <ErrorBoundary section="Press Release"><PressReleasePage /></ErrorBoundary>}</Route>
      <Route path="/scan-credits">{() => <ErrorBoundary section="Scan Credits"><ScanCreditsPage /></ErrorBoundary>}</Route>
      <Route path="/clients" component={ClientsPage} />
      <Route path="/pricing">{() => <ErrorBoundary section="Pricing"><PricingPage /></ErrorBoundary>}</Route>
      <Route path="/checkout">{() => <ErrorBoundary section="Checkout"><CheckoutPage /></ErrorBoundary>}</Route>
      <Route path="/link-gbp">{() => <ErrorBoundary section="Link GBP"><LinkGbpPage /></ErrorBoundary>}</Route>
      <Route path="/settings">{() => <ErrorBoundary section="Settings"><SettingsPage /></ErrorBoundary>}</Route>
      <Route path="/referrals">{() => <ErrorBoundary section="Referrals"><ReferralsPage /></ErrorBoundary>}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

const sidebarStyle = {
  "--sidebar-width": "17rem",
  "--sidebar-width-icon": "3rem",
};

function ImpersonationBanner() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const stopMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/admin/stop-impersonating");
    },
    onSuccess: () => {
      queryClient.clear();
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setTimeout(() => setLocation("/admin"), 200);
    },
  });

  if (!user?.impersonating) return null;

  return (
    <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between text-[13px] font-semibold shrink-0 z-50" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <span>Viewing as: {user.fullName || user.email || user.username}</span>
        <span className="text-amber-900 font-normal">({user.email})</span>
      </div>
      <button
        onClick={() => stopMutation.mutate()}
        className="bg-black/20 hover:bg-black/30 px-3 py-1 rounded text-[12px] font-bold transition-colors"
        data-testid="button-stop-impersonating"
        disabled={stopMutation.isPending}
      >
        ← Exit to Admin
      </button>
    </div>
  );
}

function PostHogIdentify() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      posthog.identify(user.id, {
        name: user.fullName,
        email: user.email || user.username,
        plan: user.tier,
        role: user.role,
      });
      posthog.register({
        client_plan: user.tier,
        user_role: user.role,
      });
    }
  }, [user?.id]);

  return null;
}

function CurrentLocationLabel({ locations }: { locations?: Location[] }) {
  const { selectedLocationId } = useLocationStore();
  const fromList = locations?.find(l => l.id === selectedLocationId) || locations?.[0];
  const { data: fetched } = useQuery<Location>({
    queryKey: ["/api/locations", selectedLocationId],
    enabled: !!selectedLocationId && !fromList,
  });
  const current = fromList || fetched;
  if (!current) return null;
  // Prefer the GBP merchant dashboard URL when we have the GBP location ID —
  // a signed-in owner lands directly on the management dashboard for this
  // exact location. Fall back to a Google search for locations without a
  // linked GBP, which still surfaces the "Your business" edit panel inline.
  const searchTerms = [current.name, current.city, current.state]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join(" ");
  // Google's /dashboard/l/{id} URL frequently loops back to login when the
  // user has multiple Google accounts. The search-based URL reliably surfaces
  // the signed-in owner's "Your business" management panel for that listing.
  const gbpUrl = searchTerms
    ? `https://www.google.com/search?q=${encodeURIComponent(searchTerms)}`
    : "https://business.google.com/";
  const addressLine = [current.address, current.city, current.state]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join(", ");
  return (
    <a
      href={gbpUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 min-w-0 px-2 py-1 rounded-md hover:bg-accent transition-colors"
      data-testid="header-current-location"
      title={`Open ${current.name} on Google (new tab)`}
    >
      <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
      <div className="flex flex-col min-w-0 leading-tight">
        <span className="text-sm font-semibold truncate" data-testid="text-header-location-name">
          {current.name}
        </span>
        {addressLine && (
          <span className="text-[11px] text-muted-foreground truncate" data-testid="text-header-location-address">
            {addressLine}
          </span>
        )}
      </div>
    </a>
  );
}

function PageTracker() {
  usePageTracking();
  useSessionDuration();
  return null;
}

/**
 * When the user finishes the last task in a phase on any feature page,
 * automatically navigate them back to /road-to-domination so the
 * celebration overlay can fire there. The RTD page itself uses the same
 * localStorage flag to decide which phase to celebrate; once dismissed,
 * the phase is marked celebrated and we will not redirect again.
 */
function PhaseCompletionRedirect() {
  const [pathname, setLocationPath] = useLocation();
  const { selectedLocationId } = useLocationStore();
  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });
  const locationId = selectedLocationId || locations?.[0]?.id || "";
  const { data: progress, isFetched } = useQuery<{ completedTasks?: string[] }>({
    queryKey: ["/api/locations", locationId, "rtd-progress"],
    enabled: !!locationId,
    refetchInterval: 15000,
  });
  // Track baseline per-location. Only initialize once the query has actually
  // loaded data for THIS location — otherwise a stale/empty payload from the
  // previous location would look like "0 tasks", and the next refresh would
  // falsely appear as forward progress.
  const baselineRef = useRef<{ locationId: string; completedKey: string } | null>(null);

  useEffect(() => {
    if (!locationId || !isFetched) return;
    const completedList = progress?.completedTasks ?? [];
    const completed = new Set(completedList);
    const completedKey = [...completedList].sort().join("|");
    const baseline = baselineRef.current;

    // First load for this location — set baseline, do not redirect.
    if (!baseline || baseline.locationId !== locationId) {
      baselineRef.current = { locationId, completedKey };
      return;
    }
    // No change since last seen — nothing to do.
    if (baseline.completedKey === completedKey) return;
    // Forward-progress check: new set is a strict superset of the old.
    const prevTasks = new Set(baseline.completedKey ? baseline.completedKey.split("|") : []);
    const addedSomething = [...completed].some((t) => !prevTasks.has(t));
    baselineRef.current = { locationId, completedKey };
    if (!addedSomething) return;

    // Don't redirect away from the RTD page or the dashboard root.
    if (pathname === "/road-to-domination" || pathname === "/") return;
    const next = getNewlyCompletedPhase(locationId, completed);
    if (next) {
      setLocationPath("/road-to-domination");
    }
  }, [locationId, isFetched, progress?.completedTasks, pathname, setLocationPath]);

  return null;
}

function AuthenticatedApp() {
  const { user } = useAuth();
  const [forceOnboarding, setForceOnboarding] = useState(() => {
    return typeof window !== "undefined" && sessionStorage.getItem("dd_setup_step") !== null;
  });
  const { data: myLocations, isLoading: locationsLoading } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const isAdmin = user?.role === "admin";
  const needsSetup = !locationsLoading && (!myLocations || myLocations.length === 0) && !isAdmin;
  const showSetup = forceOnboarding || needsSetup;

  if (locationsLoading && !forceOnboarding) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (showSetup) {
    return (
      <OnboardingContext.Provider value={{ forceOnboarding, setForceOnboarding }}>
        <ImpersonationBanner />
        <SetupPage />
      </OnboardingContext.Provider>
    );
  }

  return (
    <OnboardingContext.Provider value={{ forceOnboarding, setForceOnboarding }}>
      <TourProvider>
        <SidebarProvider style={sidebarStyle as React.CSSProperties}>
          <div className="flex h-screen w-full flex-col">
            <ImpersonationBanner />
            <div className="flex flex-1 min-h-0">
              <AppSidebar />
              <div className="flex flex-col flex-1 min-w-0">
                <header className="flex items-center justify-between gap-1 p-2 border-b h-12 shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <SidebarTrigger data-testid="button-sidebar-toggle" />
                    <CurrentLocationLabel locations={myLocations} />
                  </div>
                  <div className="flex items-center gap-1">
                    <TourLauncher />
                    <FeedbackButton />
                    <BugReporter />
                  </div>
                </header>
                <main className="flex-1 overflow-hidden">
                  <Router />
                </main>
              </div>
            </div>
          </div>
          <AriaChat userName={user?.fullName || user?.email || user?.username} />
          <TourAutoStart />
          <PhaseCompletionRedirect />
        </SidebarProvider>
      </TourProvider>
    </OnboardingContext.Provider>
  );
}

function TourLauncher() {
  const [pathname] = useLocation();
  const { startTour } = useTour();
  const tour = TOURS[pathname];
  if (!tour) return null;
  return (
    <button
      onClick={() => startTour(tour, { force: true })}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      data-testid="button-start-tour"
      title="Take the tour"
    >
      <HelpCircle className="w-4 h-4" />
      <span className="hidden sm:inline">Tour</span>
    </button>
  );
}

function TourAutoStart() {
  const [pathname] = useLocation();
  const { startTour } = useTour();
  useEffect(() => {
    const tour = TOURS[pathname];
    if (!tour) return;
    // Defer so target elements have time to mount
    const t = setTimeout(() => startTour(tour), 600);
    return () => clearTimeout(t);
  }, [pathname, startTour]);
  return null;
}

function PublicReportPage() {
  const [, params] = useRoute("/report/:token");
  return (
    <div className="min-h-screen bg-[#080c15]">
      <ClientReportPage shareToken={params?.token} />
    </div>
  );
}

function AppRouter() {
  const [isSharePage] = useRoute("/share/:token");
  const [isReportPage] = useRoute("/report/:token");
  const [isRedeemPage] = useRoute("/redeem/:code");
  const [isLoginPage] = useRoute("/login");
  const [isTrialPage] = useRoute("/trial");
  const [isBarbershopTrialPage] = useRoute("/barbershop-trial");
  const [isMetaTrialPage] = useRoute("/meta-trial");
  const [isAdminLoginPage] = useRoute("/admin-login");
  const [isHomePage] = useRoute("/");
  const [isAdminPage] = useRoute("/admin");
  const [isTestimonialsPage] = useRoute("/testimonials");
  const [isTermsPage] = useRoute("/terms");
  const [isPrivacyPage] = useRoute("/privacy");
  const [isAcceptTermsPage] = useRoute("/accept-terms");
  const [location] = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  // Meta Pixel: fire a PageView on every client-side route change. The initial
  // load is already tracked by the static pixel in index.html, so skip the
  // first run to avoid double-counting it.
  const metaPixelMounted = useRef(false);
  useEffect(() => {
    if (!metaPixelMounted.current) {
      metaPixelMounted.current = true;
      return;
    }
    trackMetaPageView();
  }, [location]);

  if (isSharePage) {
    return <SharePage />;
  }

  if (isReportPage) {
    return <PublicReportPage />;
  }

  // /redeem/<code> — friends-and-family invite link. Reachable logged-in or
  // out: the page validates the code, claims it for an authed account, or
  // stashes it and routes new users through signup.
  if (isRedeemPage) {
    return <RedeemPage />;
  }

  if (isTestimonialsPage) {
    return <TestimonialsPage />;
  }

  // /terms and /privacy are publicly reachable so people can read them
  // both before signing up and from inside the app.
  if (isTermsPage) {
    return <TermsPage />;
  }
  if (isPrivacyPage) {
    return <PrivacyPage />;
  }

  if (isHomePage) {
    return <HomePage />;
  }

  // Public SEO landing pages (for non-logged-in visitors / crawlers). The
  // `/pricing` and `/reviews` slugs also exist as authenticated app routes, so
  // for those two we only serve the SEO page to logged-out visitors and let
  // authenticated users fall through to the app's own page.
  // Normalize a trailing slash (except root) so `/slug/` still matches `/slug`.
  const seoSlug = location !== "/" && location.endsWith("/") ? location.slice(0, -1) : location;
  // Industry hub + programmatic per-niche pages (/for, /for/<niche>). These
  // never collide with app routes, so they're served to everyone; SeoNichePage
  // 404s unknown slugs internally.
  if (seoSlug === "/for") {
    return <SeoForHub />;
  }
  if (seoSlug.startsWith("/for/")) {
    return <SeoNichePage slug={seoSlug.slice("/for/".length)} />;
  }
  if (seoSlug === "/best") {
    return <SeoBestHub />;
  }
  if (seoSlug.startsWith("/best/")) {
    return <SeoBestPage slug={seoSlug.slice("/best/".length)} />;
  }
  if (TORK_SLUGS.includes(seoSlug)) {
    const conflictsWithApp = seoSlug === "/pricing" || seoSlug === "/reviews";
    if (!conflictsWithApp) {
      return <SeoPage slug={seoSlug} />;
    }
    if (!isLoading && !isAuthenticated) {
      return <SeoPage slug={seoSlug} />;
    }
    // authenticated (or still resolving): fall through to the app's own route
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // /trial — the $1-trial tracking link for new leads. Stash the lead source
  // so the Google OAuth flow forwards it (?source=trial) and tags the NEW
  // account in GHL + alerts. Existing clients who are already signed in are
  // sent straight to their dashboard and never counted as a new lead.
  if (isTrialPage) {
    if (isAuthenticated) {
      return <Redirect to="/dashboard" />;
    }
    return <TrialLanding source="trial" />;
  }

  // /barbershop-trial — same $1-trial flow, tagged for barbershop outreach.
  if (isBarbershopTrialPage) {
    if (isAuthenticated) {
      return <Redirect to="/dashboard" />;
    }
    return <TrialLanding source="barbershop-trial" />;
  }

  // /meta-trial — same $1-trial flow, tagged for Meta (Facebook/Instagram) ads
  // so new signups from those campaigns are attributed to source "meta-ads".
  if (isMetaTrialPage) {
    if (isAuthenticated) {
      return <Redirect to="/dashboard" />;
    }
    return <TrialLanding source="meta-ads" heroProps={META_TRIAL_HERO} />;
  }

  if (isLoginPage) {
    if (isAuthenticated) {
      return <Redirect to="/dashboard" />;
    }
    return <AuthPage />;
  }

  if (isAdminLoginPage) {
    if (isAuthenticated) {
      return <Redirect to="/dashboard" />;
    }
    return <AdminLoginPage />;
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  // TOS gate: any authenticated user who hasn't accepted the current TOS
  // version is locked to /accept-terms until they do (or sign out). Older
  // users get re-prompted automatically when CURRENT_TOS_VERSION bumps.
  if (user && user.tosAccepted === false && !isAcceptTermsPage) {
    return <Redirect to="/accept-terms" />;
  }
  if (isAcceptTermsPage) {
    return <AcceptTermsPage />;
  }

  if (isAdminPage) {
    if (user?.role !== "admin" && !user?.impersonating) {
      return <Redirect to="/dashboard" />;
    }
    return <AdminPage />;
  }

  return <AuthenticatedApp />;
}

// Capture ad-click identifiers (gclid/gbraid/wbraid/fbclid + utm_*) on any
// page-load and stash them in localStorage so the $1-trial Stripe checkout can
// forward them into the session metadata for offline conversion attribution.
function AttributionCapture() {
  useEffect(() => {
    captureAttribution();
    // Persist the ad-funnel contact id (?cid=) so we can match the phone-only
    // GoHighLevel lead to this signup and prefill their phone/name later.
    captureCid();
  }, []);
  return null;
}

// Capture ?ref=CODE on any page-load (homepage, /login, anywhere) and stash
// it in localStorage so the Google OAuth flow can forward it to the server.
// Kept ≤16 chars, alphanumeric only. Expires after 30 days.
function ReferralCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("ref");
      if (!raw) return;
      const clean = raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 16).toUpperCase();
      if (!clean) return;
      localStorage.setItem("dd_ref", clean);
      localStorage.setItem("dd_ref_at", String(Date.now()));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      const at = Number(localStorage.getItem("dd_ref_at") || 0);
      if (at && Date.now() - at > 30 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem("dd_ref");
        localStorage.removeItem("dd_ref_at");
      }
      const srcAt = Number(localStorage.getItem("dd_lead_source_at") || 0);
      if (srcAt && Date.now() - srcAt > 30 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem("dd_lead_source");
        localStorage.removeItem("dd_lead_source_at");
      }
    } catch {}
  }, []);
  return null;
}

// Landing for the $1-trial tracking links (e.g. /trial, /barbershop-trial).
// Stashes the lead source in localStorage (mirrors the dd_ref pattern, 30-day
// expiry) so the Google sign-in flow forwards ?source=<source> to the server,
// then renders the signup page with $1-trial copy.
function TrialLanding({ source = "trial", heroProps }: { source?: string; heroProps?: HeroCopy }) {
  useEffect(() => {
    try {
      localStorage.setItem("dd_lead_source", source);
      localStorage.setItem("dd_lead_source_at", String(Date.now()));
    } catch {}
  }, [source]);
  return <TrialLandingPage heroProps={heroProps} />;
}

// After a friend signs up/logs in via a /redeem link, the code was stashed in
// localStorage. Once they're authenticated, silently claim it so their tier is
// unlocked even though OAuth redirected them away from the redeem page.
function RedeemCapture() {
  const { isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  useEffect(() => {
    if (!isAuthenticated) return;
    let code = "";
    try {
      code = localStorage.getItem(REDEEM_STORAGE_KEY) || "";
    } catch {}
    if (!code) return;
    (async () => {
      try {
        await apiRequest("POST", `/api/redeem/${encodeURIComponent(code)}`);
        await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      } catch {
        // leave the code stashed so a later retry can pick it up
        return;
      }
      try {
        localStorage.removeItem(REDEEM_STORAGE_KEY);
      } catch {}
      navigate(`/redeem/${code}`);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  return null;
}

// After a paid Stripe trial checkout, Stripe redirects back with
// ?session_id=<cs_...> in the URL. Confirm the payment server-side (also gives
// us the buyer email), then fire the Google Ads conversion + Meta Purchase/CAPI
// exactly once per session. Mounted app-wide so it works for every success URL
// (e.g. /pricing and /setup). No-ops on every page without a session_id.
function ConversionTracker() {
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    handled.current = true;
    (async () => {
      // Only strip session_id once we have a definitive answer (conversion
      // fired, or the session is provably not ours / unknown). On transient
      // failures (network, 401, 5xx) we leave it in the URL so a reload retries
      // — the sessionStorage guard in fireTrialConversions prevents double-count.
      let settled = false;
      try {
        const res = await fetch(
          `/api/billing/checkout-session/${encodeURIComponent(sessionId)}`,
          { credentials: "include" },
        );
        if (res.ok) {
          const data = (await res.json()) as {
            paid: boolean;
            email: string | null;
            phone?: string | null;
            firstName?: string | null;
          };
          if (data.paid) {
            fireTrialConversions({ sessionId, email: data.email });
            // Tell GoHighLevel the trial was paid so it tags the contact and
            // stops the nurture drip. Best-effort, fires once per session.
            notifyTrialStarted({
              sessionId,
              phone: data.phone,
              email: data.email,
              firstName: data.firstName,
            });
          }
          settled = true;
        } else if (res.status === 403 || res.status === 404) {
          settled = true; // not our session / unknown — retrying won't help
        }
      } catch {
        // Network error — leave session_id for a retry on reload.
      } finally {
        if (settled) {
          params.delete("session_id");
          const qs = params.toString();
          window.history.replaceState(
            {},
            "",
            window.location.pathname + (qs ? `?${qs}` : ""),
          );
        }
      }
    })();
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary fallback={<GlobalErrorFallback />}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AttributionCapture />
            <ReferralCapture />
            <ConversionTracker />
            <RedeemCapture />
            <PostHogIdentify />
            <PageTracker />
            <AppRouter />
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
